import { ColorHex } from '@axafr-btc-ds/colors';
import { IconName } from '@axafr-btc-ds/icons';
import { MessageOphelie } from '@axafr-btc-ds/messages';
import { Tab, TabList } from '@axafr-btc-ds/tabs';
import { Typography } from '@axafr-btc-ds/typography';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Product, useCatalog } from 'services/catalog';
import { getQueryClient } from 'utils/query-client';
import { CatalogProps, CatalogMap } from './Catalog.interface';
import styles from './Catalog.module.css';
import { toggleProducts } from './Catalog.utils';
import { ProductList } from 'components/product-list';

const groupIconNames: Record<string, IconName> = {
    'activite': 'home-and-building',
    'dirigeant': 'bag',
    'salaries': 'head',
};

const queryClient = getQueryClient();

const Catalog = ({ data, onChange, onTechnicalError, className }: CatalogProps): JSX.Element => {
    const tabRef = useRef<HTMLDivElement>(null);
    const [ sticky, setSticky ] = useState(false);
    const { data: catalogData } = useCatalog({ onTechnicalError });

    const currentSelection: CatalogMap = data?.selectedProducts || new Map();
    const onProductToggle = (groupCode: string, value?: string[]) => {
        if (!catalogData) {
            return;
        }

        const group = catalogData.groups.find((grp) => grp.code === groupCode);
        if (!group) {
            return;
        }

        const product = group.products.filter((prd) => value?.includes(prd.codeProduit));

        const [ newSelection, newValidityStatus ] = toggleProducts(currentSelection, group, product);
        onChange?.({
            selectedProducts: newSelection,
        }, newValidityStatus);
    };

    useEffect(() => {
        const scrollToSticky = () => {
            if (tabRef.current) {
                setSticky(document.documentElement.scrollTop >= tabRef.current?.offsetTop);
            }
        };

        document.addEventListener('scroll', scrollToSticky);
        return () => {
            document.removeEventListener('scroll', scrollToSticky);
        };
    }, []);

    return (
        <div className={ classNames(styles.host, className) }>
            { data?.showOphelie && (
                <MessageOphelie className={ classNames(styles.ophelie) }>
                    Un conseiller à vos côtés !
                    Le détail des tarifs de ces produits sera affiné avec un conseiller en fonction de vos besoins.
                </MessageOphelie>
            ) }

            <div className={ styles.subtitle }>
                <Typography
                    color={ ColorHex.axablue }
                    component="h2"
                    variant="title1"
                >
                    Choisissez vos besoins d'assurances en fonction de votre secteur d'activité
                </Typography>
            </div>

            <div ref={ tabRef } className={ styles.container }>
                <div className={ styles.placeholder } />
                { catalogData && (
                    <TabList className={ classNames(styles.tabs, { [ styles.sticky ]: sticky }) }>
                        { catalogData.groups.map((group) => {
                            const defaultValue: Product['codeProduit'][] = [];
                            const products = currentSelection.get(group.code);
                            if (products) {
                                products.forEach((product) => {
                                    defaultValue.push(product.codeProduit);
                                });
                            }

                            return (
                                <Tab
                                    key={ group.code }
                                    label={ group.name }
                                    panelId={ group.name }
                                    className={ styles.tab }
                                    iconName={ groupIconNames[ group.code ] }
                                >
                                    <ProductList
                                        labelledBy={ group.name }
                                        products={ group.products }
                                        value={ defaultValue }
                                        onChange={ (value) => onProductToggle(group.code, value) }
                                    />
                                </Tab>
                            );
                        }) }
                    </TabList>
                ) }
            </div>
        </div>
    );
};

const ConnectedCatalog = (props: CatalogProps): JSX.Element => {
    return (
        <QueryClientProvider client={ queryClient }>
            <Catalog { ...props } />
        </QueryClientProvider>
    );
};

export default ConnectedCatalog;
export { ConnectedCatalog as Catalog };
